import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Chip,
    Stack,
    CircularProgress,
    Button,
    Grid,
    Paper,
    useTheme,
    Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetUser } from '../../hooks/Users/useGetUser';
import { useTranslation } from 'react-i18next';

const ModelShowUser = ({ open, onClose, UserId }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { User, isLoading } = useGetUser(UserId);
    const user = User?.data;

    const formatDateTime = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString(lng === 'ar' ? 'ar-EG' : 'en-US');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: theme.palette.boxShadow,
                    bgcolor: theme.palette.background.card,
                },
            }}
        >
            <DialogTitle
                sx={{
                    m: 0,
                    px: 3,
                    py: 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: isDark
                        ? 'rgba(80,87,255,0.08)'
                        : 'rgba(80,87,255,0.04)',
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h6">
                        {isArabic ? 'تفاصيل المستخدم' : 'User details'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: isDark
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    pt: 2,
                    pb: 3,
                    px: 3,
                    direction: isArabic ? 'rtl' : 'ltr',
                    bgcolor: theme.palette.background.form,
                }}
            >
                {isLoading || !UserId ? (
                    <Box
                        sx={{
                            py: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                ) : !user ? (
                    <Typography align="center" sx={{ py: 4 }}>
                        {isArabic
                            ? 'لا توجد بيانات متاحة لهذا المستخدم'
                            : 'No data available for this user'}
                    </Typography>
                ) : (
                    <Box>
                        {/* الكارت الأساسي */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2.5,
                                mb: 3,
                                borderRadius: 2,
                                borderColor: theme.palette.divider,
                                bgcolor: isDark
                                    ? 'rgba(255,255,255,0.02)'
                                    : 'rgba(255,255,255,0.9)',
                            }}
                        >
                            <Grid container spacing={2}>
                                {/* أفاتار + الاسم */}
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 72,
                                            height: 72,
                                            bgcolor: theme.palette.primary.main,
                                            fontSize: 28,
                                        }}
                                        src={user.image?.preview || user.image?.url || undefined}
                                    >
                                        {user.name?.charAt(0)?.toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {user.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {user.email}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* باقي البيانات */}
                                <Grid item xs={12} md={9}>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {isArabic ? 'اسم المستخدم' : 'Username'}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {user.user_name}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {isArabic ? 'رقم الجوال' : 'Phone'}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {user.phone || '-'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom

                                            >
                                                {isArabic ? 'نوع المستخدم' : 'User type'}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                sx={{ mx: 1 }}
                                                color={user.is_admin ? 'primary' : 'default'}
                                                label={
                                                    user.is_admin
                                                        ? isArabic
                                                            ? 'مدير'
                                                            : 'Admin'
                                                        : isArabic
                                                            ? 'مستخدم'
                                                            : 'User'
                                                }
                                                variant={user.is_admin ? 'filled' : 'outlined'}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom

                                            >
                                                {isArabic ? 'الحالة' : 'Status'}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                sx={{ mx: 1 }}
                                                color={user.status ? 'success' : 'error'}
                                                label={
                                                    user.status
                                                        ? isArabic
                                                            ? 'مفعّل'
                                                            : 'Active'
                                                        : isArabic
                                                            ? 'موقوف'
                                                            : 'Inactive'
                                                }
                                                variant="outlined"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {isArabic ? 'الأدوار' : 'Roles'}
                                            </Typography>
                                            {user.roles?.length ? (
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    flexWrap="wrap"
                                                >
                                                    {user.roles.map((role) => (
                                                        <Chip
                                                            key={role?.id}
                                                            size="small"
                                                            label={role?.name}
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {isArabic
                                                        ? 'لا توجد أدوار'
                                                        : 'No roles assigned'}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {isArabic ? 'تاريخ الإنشاء' : 'Created at'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDateTime(user.created_at)}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {isArabic ? 'آخر تحديث' : 'Last update'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDateTime(user.updated_at)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 1.5,
                    direction: isArabic ? 'rtl' : 'ltr',
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: isDark
                        ? 'rgba(255,255,255,0.02)'
                        : 'rgba(0,0,0,0.02)',
                }}
            >
                <Button onClick={onClose} variant="contained" color="primary">
                    {isArabic ? 'إغلاق' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModelShowUser;
